import { observer } from 'mobx-react';
import { FC } from 'react';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Page, Pagebar, PagebarTitle, PagebarActions } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useCreativeGroupActions } from '../../../modules/hooks/actions/creative-group.actions';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import {
	IPerformanceOptionsDateSegments,
	IPerformanceOptionsSortBy,
	IPerformanceOptionsSortOrder,
	PerformancesColumnsSnakeCase,
	groupByMapper,
	useReportsList,
} from '../../../modules/hooks/apis/use-reports-list';
import { availablePerformanceToMetrics } from '../../../modules/models/performance.model';
import { Outlet } from 'react-router-dom';
import { useCreativeGroupHeaders } from '../../../modules/hooks/theaders/creative-groups.headers';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { useMetrics } from '../../../modules/hooks/tools/use-metrics';
import { Card } from '@monorepo/base/src/components/card/card';
import { Chart } from '@monorepo/base/src/components/chart/chart';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'creative_groups_pagebar')] };
const tableDebugProps = { dataAttrs: [new DataAttribute('id', 'creative_groups_table')] };
const actionsDebugProps = { dataAttrs: [new DataAttribute('id', 'creative_groups_actions')] };
const chartDebugProps = { dataAttrs: [new DataAttribute('id', 'creative_groups_chart')] };

export const CreativeGroups: FC = observer(() => {
	const { creativeGroupsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const columnVisibility = usePageSettings().tableStore?.getColumnVisibility();
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();
	const reportList = creativeGroupsStore.getListStore();
	const metricsStore = creativeGroupsStore.getMetrics();
	const groupByColumns = Object.keys(columnVisibility || {})
		.map((col: string) => {
			if (typeof col === 'string' && columnVisibility && columnVisibility[col]) {
				return groupByMapper[col];
			}
		})
		.filter(val => !!val) as PerformancesColumnsSnakeCase[];

	const { fetchReports } = useReportsList(reportList, {
		groupBys: [...groupByColumns],
		reportName: 'adxCreativeGroups',
		sortBy: defaultSortBy?.[0]?.id || IPerformanceOptionsSortBy.Wins,
		sortOrder: defaultSortBy?.[0]?.desc ? IPerformanceOptionsSortOrder.Desc : IPerformanceOptionsSortOrder.Asc,
		submit: true,
		dateSegment: groupByColumns.includes(PerformancesColumnsSnakeCase.Dt)
			? IPerformanceOptionsDateSegments.Daily
			: IPerformanceOptionsDateSegments.None,
	});

	useReportsList(metricsStore, {
		groupBys: [PerformancesColumnsSnakeCase.Dt],
		reportName: 'adxCreativeGroups',
		dateSegment: IPerformanceOptionsDateSegments.Daily,
		submit: false,
	});

	const { SelectedActions, IndexActions, EntityActions, FiltersActions } = useCreativeGroupActions({
		debugProps: actionsDebugProps,
	});
	const { columns } = useCreativeGroupHeaders({ EntityActions, summary: reportList.getData()?.footer }); //summary
	const { metrics, xLabels } = useMetrics(metricsStore.getData());

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Creative Groups</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<Card full debugProps={{ dataAttrs: [new DataAttribute('id', 'chart')] }}>
				<Chart
					debugProps={chartDebugProps}
					isLoading={metricsStore.getIsLoading()}
					metrics={metrics}
					labels={xLabels}
					legendOptions={Object.keys(availablePerformanceToMetrics)}
				/>
			</Card>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={
					currentRouteWithoutSpecialChars === 'creativegroupscreate' || currentRouteWithoutSpecialChars === 'creativegroupsedit'
						? 'advertisers'
						: currentRouteWithoutSpecialChars
				}
				debugProps={tableDebugProps}
				columns={columns}
				data={reportList.getData()?.data || []}
				fetchReports={fetchReports}
				defaultSortBy={defaultSortBy}
				isLoading={creativeGroupsStore.getListStore().getIsLoading()}
				isSummary={true}>
				{() => {
					return {
						IndexActions,
						SelectedActions,
						FiltersActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
